import { t } from 'i18next';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ClassroomPlayer, ClassroomPlayerFrame } from '@/components/custom/classroom';
import { slideDecksApi } from '@/services/slideDecks';
import { getLessonPlayerType } from '@/utils/lessonPlayerType';

const POLL_INTERVAL_MS = 3000;

export default function StepVideolesson(props) {
  const { step, createLessonForm, urlError, setIsSlide, onLessonTypeChange } = props;
  const [player, setPlayer] = useState('');
  const [videoId, setVideoId] = useState();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const lessonType = createLessonForm.values.lessonType;
  const slideDeck = createLessonForm.values.slideDeck;

  useEffect(() => {
    const { player: detectedPlayer, videoId: detectedVideoId } = getLessonPlayerType(createLessonForm.values.url);

    if (detectedPlayer === 'youtube' || detectedPlayer === 'vimeo') {
      setIsSlide(false);
    } else if (detectedPlayer === 'genially') {
      setIsSlide(true);
      createLessonForm.setFieldValue('quizItemsAttributes', []);
    }

    setPlayer(detectedPlayer || '');
    setVideoId(detectedVideoId);
  }, [createLessonForm.values.url]);

  // Polling do status de conversão a cada 3s enquanto created/processing
  useEffect(() => {
    const deckId = slideDeck?.id;
    const deckStatus = slideDeck?.status;
    if (!deckId || (deckStatus !== 'created' && deckStatus !== 'processing')) return undefined;

    const intervalId = setInterval(async () => {
      try {
        const deck = await slideDecksApi.show(deckId);
        createLessonForm.setFieldValue('slideDeck', deck);
      } catch (error) {
        // Erro transitório de polling: mantém o estado atual e tenta no próximo tick
        console.error('Erro no polling do slide deck:', error);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [slideDeck?.id, slideDeck?.status]);

  const handlePdfUpload = async event => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const deck = await slideDecksApi.create(file);
      createLessonForm.setFieldValue('slideDeck', deck);
    } catch (error) {
      console.error(error);
      const title = error?.response?.data?.errors?.[0]?.title;
      toast.error(title || t('lessons.errorUploadPdf'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRetry = async () => {
    try {
      const deck = await slideDecksApi.retry(slideDeck.id);
      createLessonForm.setFieldValue('slideDeck', deck);
    } catch (error) {
      console.error(error);
      toast.error(t('lessons.errorRetryConversion'));
    }
  };

  const toggleOptions = [
    { key: 'video', label: t('lessons.toggleVideoLink') },
    { key: 'slides', label: t('lessons.toggleSlidesPdf') }
  ];

  const isConverting = slideDeck && (slideDeck.status === 'created' || slideDeck.status === 'processing');

  return (
    step === 'videoaula' && (
      <div className="page__content mb-12 animate-fade-in">
        <div className="new-questions__header">
          <h2 className="new-questions__title">{createLessonForm.values.body || t('textsCommon.lessonName')}</h2>
        </div>

        <div className="new-questions__body">
          <p className="new-questions__subtitle">{t('lessons.lessonVideoDescription')}</p>

          <div
            className="btn-group mb-4"
            role="group"
            aria-label={t('lessons.toggleLessonTypeAria')}
          >
            {toggleOptions.map(option => (
              <React.Fragment key={option.key}>
                <input
                  type="radio"
                  name="lessonType"
                  id={`lesson-type-${option.key}`}
                  autoComplete="off"
                  checked={lessonType === option.key}
                  onChange={() => onLessonTypeChange(option.key)}
                />
                <label
                  htmlFor={`lesson-type-${option.key}`}
                  className="btn btn--outline"
                >
                  {option.label}
                </label>
              </React.Fragment>
            ))}
          </div>

          {lessonType === 'video' && (
            <>
              <div className="form__row">
                <label
                  className="form__label"
                  htmlFor="url"
                >
                  {t('lessons.titleLinkVideoOrSlide')}
                </label>
                <input
                  className="form__control"
                  placeholder={t('lessons.placeholderSetLink')}
                  id="url"
                  name="url"
                  type="url"
                  value={createLessonForm.values.url}
                  onChange={e => createLessonForm.setFieldValue('url', e.target.value)}
                />
                {urlError && <span style={{ color: 'red' }}>{urlError}</span>}
              </div>
              {createLessonForm.values.url && player === 'youtube' && (
                <ClassroomPlayer>
                  <ClassroomPlayerFrame>
                    <div>
                      <iframe
                        width="560"
                        height="315"
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title="YouTube video player"
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowfullscreen
                      />
                    </div>
                  </ClassroomPlayerFrame>
                </ClassroomPlayer>
              )}
              {createLessonForm.values.url && player === 'vimeo' && (
                <ClassroomPlayer>
                  <ClassroomPlayerFrame>
                    <div>
                      <iframe
                        src={`https://player.vimeo.com/video/${videoId}`}
                        width="560"
                        height="315"
                        frameborder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowfullscreen
                      />
                    </div>
                  </ClassroomPlayerFrame>
                </ClassroomPlayer>
              )}
              {createLessonForm.values.url && player === 'genially' && (
                <ClassroomPlayer>
                  <ClassroomPlayerFrame>
                    <div>
                      <iframe
                        frameborder="0"
                        width="1200"
                        height="675"
                        style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%' }}
                        src={videoId}
                        type="text/html"
                        allowscriptaccess="always"
                        allowfullscreen="true"
                        allownetworking="all"
                      />
                    </div>
                  </ClassroomPlayerFrame>
                </ClassroomPlayer>
              )}
            </>
          )}

          {lessonType === 'slides' && (
            <>
              <fieldset className="form__row">
                <label className="form__custom-file-input">
                  <input
                    ref={fileInputRef}
                    type="file"
                    name="pdf"
                    id="pdf"
                    accept="application/pdf"
                    onChange={handlePdfUpload}
                  />
                  <span className="form__control">{t('lessons.slidesUploadPlaceholder')}</span>
                </label>
                {urlError && <span style={{ color: 'red' }}>{urlError}</span>}
              </fieldset>

              {uploading && <p className="new-questions__subtitle">{t('lessons.slidesUploading')}</p>}

              {!uploading && isConverting && <p className="new-questions__subtitle">{t('lessons.slidesConverting', { processed: slideDeck.processedPages || 0, total: slideDeck.pageCount || 0 })}</p>}

              {!uploading && slideDeck?.status === 'ready' && <p className="new-questions__subtitle text-success">{t('lessons.slidesReady', { total: slideDeck.pageCount })}</p>}

              {!uploading && slideDeck?.status === 'failed' && (
                <div className="form__row">
                  <span style={{ color: 'red' }}>
                    {t('lessons.slidesFailed')}
                    {slideDeck.errorMessage ? `: ${slideDeck.errorMessage}` : ''}
                  </span>
                  <button
                    type="button"
                    className="btn btn--outline"
                    onClick={handleRetry}
                  >
                    {t('lessons.slidesRetry')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )
  );
}
